import { useState } from 'react';
import { Authenticated, useLogout, useGetIdentity } from '@refinedev/core';
import { useAuth, useClerk } from '@clerk/react';
import { Outlet, Navigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  ButtonBase,
  CircularProgress,
  Typography,
  IconButton,
  Container,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import type { AdminScope } from '@residenone/shared';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboardOutlined';
import HomeWorkIcon from '@mui/icons-material/HomeWorkOutlined';
import SummarizeIcon from '@mui/icons-material/SummarizeOutlined';
import PeopleAltIcon from '@mui/icons-material/PeopleAltOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import DeckIcon from '@mui/icons-material/DeckOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailableOutlined';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ShieldIcon from '@mui/icons-material/ShieldOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import LocationCityIcon from '@mui/icons-material/LocationCityOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import { NotificationBell } from './components/NotificationBell';
import { ClusterSwitcher } from './components/ClusterSwitcher';
import { GlobalSearch } from './components/GlobalSearch';
import { EditProfileDialog, initialsOf } from './components/EditProfileDialog';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import { useColorMode } from './theme';
import { useLanguage } from './i18n';

const DRAWER_WIDTH = 264;

/** A single navigable link in the sidebar. */
interface NavLeaf {
  label: string;
  to: string;
  icon: SvgIconComponent;
  /** Hide behind a permission flag; with several, any one of them is enough. */
  permission?: string | string[];
  /** Restrict to these admin tiers (omit = all tiers). */
  scopes?: AdminScope[];
  adminOnly?: boolean;
}

/** A section heading over related child links (no page of its own). Always expanded. */
interface NavGroup {
  label: string;
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry;

// `label` holds an i18n key (see ./i18n); the sidebar renders it through t().
const NAV_ITEMS: NavEntry[] = [
  { label: 'nav.dashboard', to: '/dashboard', icon: SpaceDashboardIcon },
  {
    label: 'nav.peopleProperty',
    children: [
      { label: 'nav.units', to: '/units', icon: HomeWorkIcon, permission: 'can_manage_units' },
      // Residents and the cluster team share one page, so either permission opens it.
      { label: 'nav.residents', to: '/residents', icon: PeopleAltIcon, permission: ['can_manage_residents', 'can_manage_staff'] },
      { label: 'nav.peopleSummary', to: '/people/summary', icon: SummarizeIcon, permission: 'can_manage_residents' },
    ],
  },
  {
    label: 'nav.billing',
    children: [
      { label: 'nav.fees', to: '/billing/fees', icon: PaymentsIcon, permission: 'can_manage_payments' },
      { label: 'nav.invoices', to: '/invoices', icon: ReceiptLongIcon, permission: 'can_manage_payments' },
      { label: 'nav.reports', to: '/reports', icon: InsightsIcon, permission: 'can_manage_payments' },
    ],
  },
  {
    label: 'nav.amenities',
    children: [
      { label: 'nav.facilities', to: '/facilities', icon: DeckIcon, permission: 'can_manage_facilities' },
      { label: 'nav.bookings', to: '/bookings', icon: EventAvailableIcon, permission: 'can_approve_bookings' },
    ],
  },
  {
    label: 'nav.community',
    children: [
      { label: 'nav.announcements', to: '/announcements', icon: CampaignIcon, permission: 'can_post_announcements' },
      { label: 'nav.complaints', to: '/complaints', icon: FeedbackIcon, permission: 'can_manage_complaints' },
      { label: 'nav.paymentReminders', to: '/reminders', icon: NotificationsActiveIcon, permission: 'can_manage_payments' },
    ],
  },
  {
    label: 'nav.securityOps',
    children: [
      { label: 'nav.operations', to: '/operations', icon: ShieldIcon, permission: 'can_view_emergency' },
    ],
  },
  { label: 'nav.dashboard', to: '/platform/dashboard', icon: SpaceDashboardIcon, scopes: ['PLATFORM'] },
  {
    label: 'nav.platform',
    children: [
      { label: 'nav.developers', to: '/platform/developers', icon: BusinessIcon, scopes: ['PLATFORM'] },
      { label: 'nav.clusters', to: '/platform/clusters', icon: LocationCityIcon, scopes: ['PLATFORM'] },
      { label: 'nav.archived', to: '/platform/archived', icon: Inventory2Icon, scopes: ['PLATFORM'] },
    ],
  },
];

export function ProtectedLayout() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <CircularProgress sx={{ m: 4 }} />;
  if (!isSignedIn) return <Navigate to="/login" replace />;
  return (
    <Authenticated key="protected-layout" loading={<CircularProgress sx={{ m: 4 }} />}>
      <LayoutShell />
    </Authenticated>
  );
}

interface Identity {
  name?: string;
  email?: string;
  avatar?: string;
  permissions?: string[];
  admin_scope?: AdminScope;
  app_role?: 'ADMIN' | 'INTERNAL' | 'RESIDENT';
  role?: 'STAFF' | 'SECURITY' | 'RESIDENT';
}

/**
 * The header's role line. Admins are stored as staff with admin rights, so
 * check the admin tier first; otherwise a cluster admin would read "Staf".
 */
function roleKey(identity: Identity): string {
  if (identity.admin_scope === 'PLATFORM') return 'team.platformAdmin';
  if (identity.admin_scope === 'DEVELOPER') return 'team.companyAdmin';
  if (identity.role === 'STAFF' && identity.app_role === 'ADMIN') return 'team.clusterAdmin';
  return `role.${identity.role}`;
}

function LayoutShell() {
  const theme = useTheme();
  const { mode, toggle: toggleColorMode } = useColorMode();
  const { lang, setLang, t } = useLanguage();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const [langAnchor, setLangAnchor] = useState<HTMLElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const { mutate: logout } = useLogout();
  const clerk = useClerk();
  const { data: identity, isLoading: identityLoading, refetch: refetchIdentity } = useGetIdentity<Identity>();
  const location = useLocation();
  const permissions = new Set(identity?.permissions ?? []);
  const scope = identity?.admin_scope;
  // Platform admins are confined to the User Management + Platform areas — they
  // don't operate inside any single cluster (Dashboard, Billing, Community,
  // Resident-app branding, …).
  const PLATFORM_ALLOWED_PREFIXES = ['/users', '/platform'];
  const platformAllowed = (to: string) => PLATFORM_ALLOWED_PREFIXES.some((p) => to.startsWith(p));
  const leafVisible = (leaf: NavLeaf) =>
    (!leaf.permission || [leaf.permission].flat().some((p) => permissions.has(p))) &&
    (!leaf.scopes || (!!scope && leaf.scopes.includes(scope))) &&
    (!leaf.adminOnly || identity?.app_role === 'ADMIN' || scope === 'PLATFORM') &&
    (scope !== 'PLATFORM' || platformAllowed(leaf.to));

  const routeLeaves = NAV_ITEMS.flatMap((entry) => isGroup(entry) ? entry.children : [entry]);
  const activeGuard = routeLeaves.find((leaf) => location.pathname === leaf.to || location.pathname.startsWith(`${leaf.to}/`));
  // Breadcrumb: the current page's section (if any) and the page itself.
  const activeGroup = NAV_ITEMS.find((entry): entry is NavGroup => isGroup(entry) && entry.children.includes(activeGuard as NavLeaf));

  const isActive = (to: string) => location.pathname.startsWith(to);

  // Enforce the platform limit at the route level: a platform admin who lands on
  // (or types) any non-allowed path is bounced to the User Management hub.
  if (!identityLoading && scope === 'PLATFORM' && !platformAllowed(location.pathname)) {
    return <Navigate to="/platform/dashboard" replace />;
  }
  if (!identityLoading && activeGuard && !leafVisible(activeGuard)) return <Navigate to="/dashboard" replace />;
  // Deny direct links to routes not represented in the navigation for internal users.
  if (!identityLoading && identity?.app_role === 'INTERNAL' && location.pathname !== '/dashboard' && !activeGuard) {
    return <Navigate to="/dashboard" replace />;
  }

  // Icon + label. The current page is bold, takes the accent colour, and gets
  // a rounded bar on the sidebar's right edge.
  const renderLeaf = (item: NavLeaf) => {
    const active = isActive(item.to);
    const Icon = item.icon;
    return (
      <ListItemButton
        key={item.to}
        component={RouterLink}
        to={item.to}
        selected={active}
        onClick={() => setMobileOpen(false)}
        sx={{
          borderRadius: 2, mx: 1.5, mb: 0.25, px: 1.5, py: 0.875, minHeight: 40,
          position: 'relative', overflow: 'visible',
          color: active ? 'text.primary' : 'text.secondary',
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
          '&.Mui-selected': { bgcolor: alpha(theme.palette.secondary.main, mode === 'dark' ? 0.16 : 0.08), boxShadow: 'none' },
          '&.Mui-selected:hover': { bgcolor: alpha(theme.palette.secondary.main, mode === 'dark' ? 0.22 : 0.12) },
          ...(active ? {
            '&::after': {
              content: '""', position: 'absolute', right: -12, top: 6, bottom: 6,
              width: 4, borderRadius: 2, bgcolor: 'secondary.main',
            },
          } : {}),
        }}
      >
        <ListItemIcon sx={{ minWidth: 34, color: active ? 'secondary.main' : 'inherit' }}>
          <Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t(item.label)}
          primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 500 }}
        />
      </ListItemButton>
    );
  };

  // Headings are the quiet part: small, grey, uppercase, not clickable.
  // Spacing between groups does the separating.
  const renderGroup = (group: NavGroup) => {
    const children = group.children.filter(leafVisible);
    if (children.length === 0) return null;
    return (
      <Box key={group.label} component="li" sx={{ listStyle: 'none', mt: 2 }}>
        <Typography
          component="div"
          sx={{
            px: 3,
            pb: 0.5,
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            opacity: 0.8,
          }}
        >
          {t(group.label)}
        </Typography>
        <List disablePadding>{children.map((c) => renderLeaf(c))}</List>
      </Box>
    );
  };

  // Logo on top, then the menu (scrolls on its own).
  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
        <Box
          component={RouterLink}
          to={scope === 'PLATFORM' ? '/platform/dashboard' : '/dashboard'}
          onClick={() => setMobileOpen(false)}
          sx={{ display: 'inline-flex' }}
        >
          {/* Navy lockup (mark + wordmark); rendered white on the dark theme. */}
          <Box
            component="img"
            src="/logo-lockup.png"
            alt="ResidenOne"
            sx={{ height: 44, width: 'auto', filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'none' }}
          />
        </Box>
      </Box>
      <Divider sx={{ mx: 3 }} />
      {/* Scrolls without a visible scrollbar, which would sit on the active-page bar. */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', pb: 2, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        <List sx={{ pt: 1.5 }}>
          {scope !== 'PLATFORM' ? (
            <Box component="li" sx={{ listStyle: 'none', px: 2, pb: 1 }}>
              <ClusterSwitcher />
            </Box>
          ) : null}
          {NAV_ITEMS.map((entry) => {
            if (isGroup(entry)) return renderGroup(entry);
            if (!leafVisible(entry)) return null;
            return renderLeaf(entry);
          })}
        </List>
      </Box>
    </Box>
  );

  // Language, theme, notifications and the account, floating on the top right.
  const actionsPill = (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 0.75,
        pl: 2.25,
        pr: 1.25,
        borderRadius: 999,
        bgcolor: 'background.paper',
        boxShadow: mode === 'dark' ? 'none' : '0 14px 36px rgba(28,27,25,0.10)',
        border: mode === 'dark' ? 1 : 0,
        borderColor: 'divider',
      }}
    >
      {scope === 'PLATFORM' ? <GlobalSearch /> : null}
      <Tooltip title={lang === 'id' ? 'Bahasa' : 'Language'}>
        <IconButton size="small" color="inherit" onClick={(e) => setLangAnchor(e.currentTarget)} aria-label="language">
          <TranslateIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={langAnchor}
        open={Boolean(langAnchor)}
        onClose={() => setLangAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem selected={lang === 'id'} onClick={() => { setLang('id'); setLangAnchor(null); }}>
          Indonesia
        </MenuItem>
        <MenuItem selected={lang === 'en'} onClick={() => { setLang('en'); setLangAnchor(null); }}>
          English
        </MenuItem>
      </Menu>
      <IconButton size="small" color="inherit" onClick={toggleColorMode} aria-label="toggle dark mode">
        {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
      </IconButton>
      <NotificationBell />
      {/* Name with the role under it, then the avatar; all of it opens the account menu. */}
      <ButtonBase
        onClick={(e) => setAccountAnchor(e.currentTarget)}
        aria-label="account menu"
        sx={{ gap: 1.25, ml: 1.5, pl: 1, pr: 0.25, py: 0.25, borderRadius: 999, '&:hover': { bgcolor: 'action.hover' } }}
      >
        {/* On phones only the avatar fits; the menu still shows the name. */}
        <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', maxWidth: 180 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.25 }}>
            {identity?.name ?? t('common.account')}
          </Typography>
          {identity?.role ? (
            <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.25 }}>
              {t(roleKey(identity))}
            </Typography>
          ) : null}
        </Box>
        <Avatar
          src={identity?.avatar}
          sx={{ width: 36, height: 36, fontSize: 14, bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
        >
          {initialsOf(identity?.name) || undefined}
        </Avatar>
      </ButtonBase>
      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 1, borderRadius: 3 } } }}
      >
        {/* Who is signed in, since the pill only shows the avatar. */}
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" noWrap>{identity?.name ?? t('common.account')}</Typography>
          {identity?.email ? (
            <Typography variant="caption" color="text.secondary" noWrap component="div">{identity.email}</Typography>
          ) : null}
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAccountAnchor(null);
            setEditOpen(true);
          }}
        >
          <ListItemIcon>
            <ManageAccountsIcon fontSize="small" />
          </ListItemIcon>
          {t('common.editProfile')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAccountAnchor(null);
            clerk.openUserProfile();
          }}
        >
          <ListItemIcon>
            <ManageAccountsIcon fontSize="small" />
          </ListItemIcon>
          {t('common.manageAccount')}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAccountAnchor(null);
            logout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('common.logout')}
        </MenuItem>
      </Menu>
    </Paper>
  );

  return (
    // App shell: a full-height sidebar on the left, and the page on the right
    // with a frosted header row (breadcrumb + floating actions) pinned on top.
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile: temporary drawer toggled by the hamburger */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', borderRadius: '0 20px 20px 0' },
        }}
      >
        {sidebar}
      </Drawer>

      {/* Desktop: static full-height sidebar */}
      <Box
        component="nav"
        aria-label="main navigation"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRight: mode === 'dark' ? 1 : 0,
          borderColor: 'divider',
          boxShadow: mode === 'dark' ? 'none' : '4px 0 24px rgba(28,27,25,0.04)',
          zIndex: 1,
        }}
      >
        {sidebar}
      </Box>

      {/* Page — scrolls on its own; the header row sticks to its top */}
      {/* Column, so the credits sit at the bottom even when a page is short. */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            px: { xs: 1.5, md: 3 },
            pt: { xs: 1, md: 1.5 },
            pb: 1,
            // Frosted across the whole strip, so scrolled content never shows above it.
            bgcolor: alpha(theme.palette.background.default, 0.78),
            backdropFilter: 'blur(14px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: { xs: 0.5, md: 2 },
              pr: 0.5,
              py: 0.5,
            }}
          >
            {!isDesktop ? (
              <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label={t('common.openNav')}>
                <MenuIcon />
              </IconButton>
            ) : null}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {activeGroup ? `${t(activeGroup.label)} / ` : ''}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {activeGuard ? t(activeGuard.label) : ''}
                </Box>
              </Typography>
            </Box>
            {actionsPill}
          </Box>
        </Box>
        <Container maxWidth="xl" sx={{ flexGrow: 1, pt: 1, pb: 4, px: { xs: 2, md: 4 } }}>
          <Outlet />
        </Container>
        {/* Credits at the end of every page: copyright centred, build id on the right. */}
        <Box
          component="footer"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 2, md: 4 }, pb: 2.5, color: 'text.secondary' }}
        >
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption">© {new Date().getFullYear()} PT Semesta Digital Integrasi</Typography>
          <Typography variant="caption" sx={{ flex: 1, textAlign: 'right', opacity: 0.7 }}>v{__APP_VERSION__}</Typography>
        </Box>
      </Box>

      <EditProfileDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        currentName={identity?.name}
        currentAvatar={identity?.avatar}
        onUpdated={() => refetchIdentity()}
      />
    </Box>
  );
}
